/**
 * Thin typed facade around `window.localStorage`.
 *
 * - JSON-(de)serialises values
 * - Swallows quota / private-mode errors (later wired through telemetry)
 * - Returns null on any read failure
 *
 * No `localStorage.*` call should exist outside this module.
 */
const safeParse = <T>(raw: string): T | null => {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const storage = {
  get<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return null;
      return safeParse<T>(raw);
    } catch {
      return null;
    }
  },

  /**
   * Read the raw, un-parsed string value of a key. Used only by legacy-
   * data migrations that need to detect non-JSON values written by older
   * versions of the app.
   */
  getRaw(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // intentionally swallow (QuotaExceededError, private-mode SecurityError)
    }
  },

  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },

  clearByPrefix(prefix: string): void {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k !== null && k.startsWith(prefix)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  },
};
